/**
 * Whiteboard Service (v2)
 * Shared board state, socket sync, batching, undo and redo.
 * Points are stored as fractions of the board (0 to 1), so every screen size draws the same board.
 */

const FLUSH_MS = 40;
export const NOTE_COLOR = '#FDE68A';

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function toPx(s, W, H) {
  const P = (p) => ({ x: p.x * W, y: p.y * H });
  return {
    ...s,
    points: (s.points || []).map(P),
    from: s.from ? P(s.from) : null,
    to: s.to ? P(s.to) : null,
  };
}

function wrapLines(ctx, text, maxW) {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = t;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Draw strokes (fractions of the board) onto a canvas of size W x H. */
export function drawStrokes(ctx, strokes, preview, W, H) {
  const all = preview ? [...strokes, preview] : strokes;
  for (const s of all) drawOne(ctx, toPx(s, W, H));
}

function drawOne(ctx, s) {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = s.size || 3;
  ctx.strokeStyle = s.tool === 'eraser' ? '#FFFFFF' : s.color;
  ctx.fillStyle = s.color;

  if (s.tool === 'highlight') {
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = (s.size || 3) * 4;
  }

  if (s.tool === 'pen' || s.tool === 'eraser' || s.tool === 'highlight') {
    const pts = s.points || [];
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (pts.length > 1) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
  } else if (s.tool === 'text' && s.from) {
    ctx.font = `${Math.max(12, (s.size || 3) * 5)}px system-ui, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillText(s.text, s.from.x, s.from.y);
  } else if (s.tool === 'note' && s.from) {
    const fs = 12;
    const pad = 8;
    const side = 120;
    ctx.font = `${fs}px system-ui, sans-serif`;
    ctx.textBaseline = 'top';
    const lines = wrapLines(ctx, s.text, side - pad * 2);
    const maxLines = Math.floor((side - pad * 2) / (fs * 1.3));
    ctx.shadowColor = 'rgba(0,0,0,.25)';
    ctx.shadowBlur = 8;
    ctx.fillStyle = s.color;
    ctx.fillRect(s.from.x, s.from.y, side, side);
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#111827';
    lines.slice(0, maxLines).forEach((ln, i) => ctx.fillText(ln, s.from.x + pad, s.from.y + pad + i * fs * 1.3));
  } else if (s.from && s.to) {
    const { from, to } = s;
    ctx.beginPath();
    if (s.tool === 'line') {
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
    } else if (s.tool === 'rect') {
      ctx.rect(from.x, from.y, to.x - from.x, to.y - from.y);
    } else if (s.tool === 'ellipse') {
      ctx.ellipse(
        (from.x + to.x) / 2,
        (from.y + to.y) / 2,
        Math.abs(to.x - from.x) / 2,
        Math.abs(to.y - from.y) / 2,
        0,
        0,
        Math.PI * 2
      );
    } else if (s.tool === 'arrow') {
      const ang = Math.atan2(to.y - from.y, to.x - from.x);
      const head = 12 + (s.size || 3) * 2;
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(to.x - head * Math.cos(ang - Math.PI / 6), to.y - head * Math.sin(ang - Math.PI / 6));
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(to.x - head * Math.cos(ang + Math.PI / 6), to.y - head * Math.sin(ang + Math.PI / 6));
    }
    ctx.stroke();
  }
  ctx.restore();
}

class WhiteboardService {
  constructor() {
    this.socket = null;
    this.roomId = null;
    this.userName = 'Guest';
    this.strokes = [];
    this.open = false;
    this.allowAll = false;
    this.isHost = false;
    this.canDraw = false;
    this.redoStack = [];
    this.active = null; // { stroke, shape, pending }
    this.noteDrag = null; // { id, offset } while a note is being moved
    this.flushTimer = null;
    this.handlers = {};
    this.subscribers = new Set();
  }

  subscribe(fn) {
    this.subscribers.add(fn);
    fn(this.snapshot());
    return () => {
      this.subscribers.delete(fn);
    };
  }

  snapshot() {
    return {
      strokes: this.strokes,
      preview: this.active && this.active.shape ? this.active.stroke : null,
      open: this.open,
      allowAll: this.allowAll,
      isHost: this.isHost,
      canDraw: this.canDraw,
    };
  }

  notify() {
    const s = this.snapshot();
    this.subscribers.forEach((fn) => fn(s));
  }

  recalc() {
    this.canDraw = this.isHost || this.allowAll;
  }

  attach(socket, roomId, userName) {
    Object.entries(this.handlers).forEach(([ev, fn]) => this.socket && this.socket.off(ev, fn));
    this.handlers = {
      'wb-history': (d) => {
        this.strokes = Array.isArray(d.strokes) ? d.strokes : [];
        this.open = !!d.open;
        this.allowAll = !!d.allowAll;
        this.recalc();
        this.notify();
      },
      'wb-stroke': (s) => {
        if (!this.strokes.some((x) => x.id === s.id)) this.strokes.push(s);
        this.notify();
      },
      'wb-points': (d) => {
        const s = this.strokes.find((x) => x.id === d.id);
        if (s) s.points.push(...(d.points || []));
        this.notify();
      },
      'wb-remove': (d) => {
        this.strokes = this.strokes.filter((x) => x.id !== d.id);
        this.notify();
      },
      'wb-clear': () => {
        this.strokes = [];
        this.notify();
      },
      'wb-perm': (d) => {
        this.allowAll = !!d.allowAll;
        this.recalc();
        this.notify();
      },
      'wb-state': (d) => {
        this.open = !!d.open;
        this.notify();
      },
      'wb-moved': (d) => {
        const s = this.strokes.find((x) => x.id === d.id);
        if (s && s.tool === 'note') s.from = { x: d.x, y: d.y };
        this.notify();
      },
    };
    this.socket = socket;
    this.roomId = roomId;
    this.userName = userName || 'Guest';
    Object.entries(this.handlers).forEach(([ev, fn]) => socket.on(ev, fn));
    socket.emit('wb-request-history', { roomId });
  }

  setHost(isHost) {
    this.isHost = !!isHost;
    this.recalc();
    this.notify();
  }

  setOpen(open) {
    if (!this.isHost || !this.socket) return;
    this.socket.emit('wb-open', { roomId: this.roomId, open: !!open });
  }

  setAllowAll(allowAll) {
    if (!this.isHost || !this.socket) return;
    this.socket.emit('wb-allow-all', { roomId: this.roomId, allowAll: !!allowAll });
  }

  beginStroke(tool, color, size, p) {
    const freehand = ['pen', 'highlight', 'eraser'];
    const shapes = ['line', 'rect', 'ellipse', 'arrow'];
    if (!this.canDraw || !this.socket) return;
    if (!freehand.includes(tool) && !shapes.includes(tool)) return;
    this.redoStack = [];
    const stroke = { id: uid(), tool, color, size, author: this.userName, authorId: this.socket.id };
    if (freehand.includes(tool)) {
      stroke.points = [{ x: p.x, y: p.y }];
      this.strokes.push(stroke);
      this.socket.emit('wb-stroke', {
        roomId: this.roomId,
        stroke: { ...stroke, points: [{ x: p.x, y: p.y }] },
      });
      this.active = { stroke, shape: false, pending: [] };
    } else {
      stroke.from = { x: p.x, y: p.y };
      stroke.to = { x: p.x, y: p.y };
      this.active = { stroke, shape: true, pending: [] };
    }
    this.notify();
  }

  moveStroke(p) {
    if (!this.active) return;
    const { stroke, shape } = this.active;
    if (shape) {
      stroke.to = { x: p.x, y: p.y };
    } else {
      const last = stroke.points[stroke.points.length - 1];
      if (Math.abs(p.x - last.x) + Math.abs(p.y - last.y) < 0.002) return;
      const point = { x: p.x, y: p.y };
      stroke.points.push(point);
      this.active.pending.push(point);
      this.scheduleFlush();
    }
    this.notify();
  }

  scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, FLUSH_MS);
  }

  flush() {
    if (!this.active || !this.active.pending.length || !this.socket) return;
    this.socket.emit('wb-points', {
      roomId: this.roomId,
      id: this.active.stroke.id,
      points: this.active.pending,
    });
    this.active.pending = [];
  }

  endStroke() {
    if (!this.active) return;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const { stroke, shape } = this.active;
    if (shape) {
      this.strokes.push(stroke);
      this.socket.emit('wb-stroke', { roomId: this.roomId, stroke });
    } else {
      this.flush();
    }
    this.active = null;
    this.notify();
  }

  addText(x, y, text, color, size) {
    const t = String(text || '').trim();
    if (!t || !this.canDraw || !this.socket) return;
    this.redoStack = [];
    const stroke = {
      id: uid(),
      tool: 'text',
      color,
      size,
      author: this.userName,
      authorId: this.socket.id,
      from: { x, y },
      text: t,
    };
    this.strokes.push(stroke);
    this.socket.emit('wb-stroke', { roomId: this.roomId, stroke });
    this.notify();
  }

  addNote(x, y, text, color) {
    const t = String(text || '').trim();
    if (!t || !this.canDraw || !this.socket) return;
    this.redoStack = [];
    const stroke = {
      id: uid(),
      tool: 'note',
      color,
      size: 3,
      author: this.userName,
      authorId: this.socket.id,
      from: { x, y },
      text: t,
    };
    this.strokes.push(stroke);
    this.socket.emit('wb-stroke', { roomId: this.roomId, stroke });
    this.notify();
  }

  /** The note under point p (fractions of the board, W and H in pixels), or null. Notes are 120 px squares. */
  findNoteAt(p, W, H) {
    const side = 120;
    for (let i = this.strokes.length - 1; i >= 0; i--) {
      const s = this.strokes[i];
      if (s.tool !== 'note' || !s.from) continue;
      const x0 = s.from.x * W;
      const y0 = s.from.y * H;
      const px = p.x * W;
      const py = p.y * H;
      if (px >= x0 && px <= x0 + side && py >= y0 && py <= y0 + side) return s;
    }
    return null;
  }

  /** Start dragging a note. Only the host, or the note's own author, may move it. */
  beginNoteMove(note, p) {
    if (!this.canDraw || !this.socket || !note || !note.from) return false;
    if (!this.isHost && note.authorId !== this.socket.id) return false;
    this.noteDrag = {
      id: note.id,
      offset: { x: p.x - note.from.x, y: p.y - note.from.y },
    };
    return true;
  }

  moveNote(p) {
    if (!this.noteDrag) return;
    const s = this.strokes.find((x) => x.id === this.noteDrag.id);
    if (!s) return;
    const { offset } = this.noteDrag;
    s.from = {
      x: Math.min(Math.max(p.x - offset.x, 0), 1),
      y: Math.min(Math.max(p.y - offset.y, 0), 1),
    };
    this.notify();
  }

  /** Finish the drag: save the new position on the server, which tells everyone else. */
  endNoteMove() {
    if (!this.noteDrag) return;
    const s = this.strokes.find((x) => x.id === this.noteDrag.id);
    this.noteDrag = null;
    if (s && s.from && this.socket) {
      this.socket.emit('wb-move', { roomId: this.roomId, id: s.id, x: s.from.x, y: s.from.y });
    }
  }

  undo() {
    if (!this.socket || !this.canDraw) return;
    for (let i = this.strokes.length - 1; i >= 0; i--) {
      if (this.strokes[i].authorId === this.socket.id) {
        const [stroke] = this.strokes.splice(i, 1);
        this.redoStack.push(stroke);
        this.socket.emit('wb-undo', { roomId: this.roomId });
        this.notify();
        return;
      }
    }
  }

  redo() {
    if (!this.socket || !this.canDraw) return;
    const stroke = this.redoStack.pop();
    if (!stroke) return;
    this.strokes.push(stroke);
    this.socket.emit('wb-redo', { roomId: this.roomId, stroke });
    this.notify();
  }

  clear() {
    if (!this.isHost || !this.socket) return;
    this.socket.emit('wb-clear', { roomId: this.roomId });
  }
}

const whiteboardService = new WhiteboardService();
export default whiteboardService;
